// src/pages/ProductDetails.jsx
import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { fetchProduct } from '../lib/products'
import { addToCart } from '../lib/cartStore'
import { useUiFeedback } from '../context/UiFeedbackContext'
import { Helmet } from 'react-helmet-async'
import { formatSentenceCase } from "../utils/textFormat";
import { trackEvent } from '../utils/metaPixel'
import { addDoc, collection } from 'firebase/firestore'
import { db } from '../firebase'
// rail de produits par catégorie
import CategoryProductsStrip from '../components/CategoryProductsStrip'

const FALLBACK_IMG =
  'https://via.placeholder.com/800x600?text=Image+indisponible'

// petit helper pour un slug SEO
function slugify(str = '') {
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export default function ProductDetails() {
  const { pid } = useParams()
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { notifyAddToCart } = useUiFeedback()

  const [qty, setQty] = useState(0)
  const [activeImgIndex, setActiveImgIndex] = useState(0)

  useEffect(() => {
    if (!pid) return

      ; (async () => {
        setLoading(true)
        setError(null)
        try {
          const data = await fetchProduct(pid)
          setItem(data || null)
          setQty(0)
          setActiveImgIndex(0)
        } catch (e) {
          console.error(e)
          setError(e?.message || 'Erreur de chargement du produit.')
        } finally {
          setLoading(false)
        }
      })()
  }, [pid])

  useEffect(() => {
  if (!pid || !item) return;

  const source = new URLSearchParams(window.location.search).get("src");
  const safePrice = Number(item.price ?? item.SalePrice ?? item.salePrice ?? 0);
  const safeTitle = item.title || item.Name || item.name || item.Title || "Produit";

  trackEvent("ViewContent", {
    content_ids: [pid],
    content_name: safeTitle,
    value: safePrice,
    currency: "XOF",
  });

  addDoc(collection(db, "trackingLogs"), {
    type: "view",
    source: source || "organic",
    productId: pid,
    productName: safeTitle,
    price: safePrice,
    createdAt: new Date(),
  }).catch(console.error);

}, [pid, item]);

  if (loading) {
    return (
      <div className="container py-4">
        <div className="alert alert-info">Chargement du produit…</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger">Erreur : {error}</div>
      </div>
    )
  }

  if (!item) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">Produit introuvable.</div>
      </div>
    )
  }

  // ------- Normalisation -------
  const price = Number(item.price ?? item.SalePrice ?? item.salePrice ?? 0)
  const oldPrice = Number(
    item.Price && price && price < item.Price ? item.Price : 0
  )

  const title =
    item.title ||
    item.Name ||
    item.name ||
    item.Title ||
    'Produit'

  const desc =
    item.Description ||
    item.description ||
    item.longDescription ||
    'Pas de description disponible pour ce produit.'

  const brand = item.Brand || item.brand || item.Marque || null
  const sku = item.SKU || item.sku || null
  const upc = item.Upc || item.UPC || item.upc || null
  const vendorId = item.vendorId || item.VendorId || 'goshop'
  const vendorName = item.vendorName || item.VendorName || 'GoShop'

  // ------- Catégorie principale (rails en bas de page) -------
  const mainCategoryId =
    item.categoryId ||
    item.CategoryId ||
    item.CategorieId ||
    (item.Categorie && item.Categorie.Id) ||
    null

  const mainCategoryLabel =
    (item.Categorie && item.Categorie.Label) ||
    item.categoryLabel ||
    item.CategoryLabel ||
    null

  // ------- Images -------
  // --------- Images ----------
  // On compose un tableau d'images à partir des différents champs possibles
  let images = []

  // 1) Tableau "Images" (comme dans ta capture)
  if (Array.isArray(item.Images) && item.Images.length > 0) {
    images = item.Images
  }

  // 2) Champ unique "ImageUrl" (on le met en première position)
  if (item.ImageUrl && typeof item.ImageUrl === 'string') {
    images = [item.ImageUrl, ...images]
  }

  // 3) fallback sur imageUrl / images si besoin
  if (images.length === 0) {
    if (Array.isArray(item.ImageUrl) && item.ImageUrl.length > 0) {
      images = item.ImageUrl
    } else if (item.imageUrl) {
      images = [item.imageUrl]
    } else if (Array.isArray(item.images) && item.images.length > 0) {
      images = item.images
    } else {
      images = [FALLBACK_IMG]
    }
  }

  const mainImg = images[activeImgIndex] || images[0] || FALLBACK_IMG

  // let images = []
  // if (Array.isArray(item.ImageUrl) && item.ImageUrl.length > 0) {
  //   images = item.ImageUrl
  // } else if (item.imageUrl) {
  //   images = [item.imageUrl]
  // } else if (Array.isArray(item.images) && item.images.length > 0) {
  //   images = item.images
  // } else {
  //   images = [FALLBACK_IMG]
  // }

  // const mainImg = images[activeImgIndex] || images[0] || FALLBACK_IMG

  // ------- URL SEO (slug + canonique) -------
  const slug = slugify(title)
  const productUrl = `https://goshopforall.com/ip/${slug}/${item.id}`

  // ------- Gestion panier -------
  const changeQty = (delta) => {
    setQty((prev) => {
      const next = Math.max(prev + delta, 0)

      if (delta !== 0) {
        addToCart({
          pid: item.id,
          vendorId,
          qty: delta,
        })

        if (delta > 0 && notifyAddToCart) {
          notifyAddToCart({
            title,
            qty: delta,
            price,
          })
        }
      }
      return next
    })
  }

  // WhatsApp
  const waText = `Bonjour, je suis intéressé(e) par le produit "${title}" au prix de ${price.toLocaleString()} XOF (réf: ${pid}).`
  const waUrl = `https://wa.me/22899740138?text=${encodeURIComponent(waText)}`

  return (
    <div className="container py-4 product-page">
      {/* ----------- SEO / HEAD TAGS ----------- */}
      <Helmet>
        <title>{formatSentenceCase(title)} | GoShop</title>

        <meta name="description" content={desc.slice(0, 160)} />

        {/* Canonical URL */}
        <link rel="canonical" href={productUrl} />

        {/* Open Graph (Facebook / WhatsApp) */}
        <meta property="og:title" content={title} />
        <meta property="og:description" content={desc.slice(0, 160)} />
        <meta property="og:image" content={mainImg} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={productUrl} />

        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={desc.slice(0, 160)} />
        <meta name="twitter:image" content={mainImg} />

        {/* Schema.org Product Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org/',
            '@type': 'Product',
            name: title,
            image: mainImg,
            description: desc,
            brand: brand || 'Unknown',
            sku: sku || '',
            mpn: upc || '',
            offers: {
              '@type': 'Offer',
              priceCurrency: 'XOF',
              price: price,
              availability: 'https://schema.org/InStock',
              url: productUrl,
            },
          })}
        </script>
      </Helmet>

      {/* Fil d'Ariane */}
      <nav className="small mb-2 text-muted">
        <Link to="/catalog" className="text-decoration-none text-muted">
          Catalogue
        </Link>{' '}
        / <span>{formatSentenceCase(title)}</span>
      </nav>

      <div className="row g-4">
        {/* =========== COLONNE GAUCHE : IMAGES =========== */}
        {/* ================== COLONNE GAUCHE : IMAGES ================== */}
        <div className="col-lg-6">
          <div className="pd-media card border-0 shadow-sm">
            <div className="pd-media-inner">
              {/* Rail vertical de miniatures (desktop / tablette) */}
              {images.length > 1 && (
                <div className="pd-thumbs-rail d-none d-md-flex flex-column gap-2">
                  {images.map((src, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={
                        'pd-thumb-btn' + (idx === activeImgIndex ? ' active' : '')
                      }
                      onClick={() => setActiveImgIndex(idx)}
                    >
                      <img
                        src={src}
                        alt=""
                        className="pd-thumb-img"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}

              {/* Image principale */}
              <div className="pd-img-main-wrapper">
                <img
                  src={mainImg || FALLBACK_IMG}
                  alt={title}
                  className="pd-img-main"
                  onError={(e) => {
                    e.currentTarget.src = FALLBACK_IMG
                  }}
                />
              </div>
            </div>

            {/* Rail horizontal de miniatures pour mobile (en bas) */}
            {images.length > 1 && (
              <div className="pd-thumbs-wrapper d-flex d-md-none px-3 pb-3 pt-2">
                <div className="d-flex gap-2 flex-nowrap w-100 overflow-auto">
                  {images.map((src, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={
                        'pd-thumb-btn' + (idx === activeImgIndex ? ' active' : '')
                      }
                      onClick={() => setActiveImgIndex(idx)}
                    >
                      <img
                        src={src}
                        alt=""
                        className="pd-thumb-img"
                        onError={(e) => {
                          e.currentTarget.src = FALLBACK_IMG
                        }}
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>


        {/* =========== COLONNE DROITE : INFOS PRODUIT =========== */}
        <div className="col-lg-6">
          <div className="d-flex flex-column h-100">
            <div className="mb-2">
              {brand && (
                <div className="small text-muted mb-1">
                  Marque:{' '}
                  <span className="fw-semibold text-dark">{brand}</span>
                </div>
              )}
              <h1 className="product-title h4 h3-md mb-2">{formatSentenceCase(title)}</h1>
            </div>

            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body">
                <div className="d-flex align-items-baseline gap-2 mb-1">
                  <div className="h3 mb-0 text-success">
                    {price.toLocaleString()} XOF
                  </div>
                  {oldPrice > 0 && (
                    <div className="small text-muted text-decoration-line-through">
                      {oldPrice.toLocaleString()} XOF
                    </div>
                  )}
                </div>
                <div className="small text-muted mb-2">
                  TVA incluse si applicable. Prix indicatif.
                </div>

                <div className="d-flex flex-wrap align-items-center gap-3 mt-3">
                  {qty === 0 ? (
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={() => changeQty(+1)}
                    >
                      + Ajouter au panier
                    </button>
                  ) : (
                    <div className="qty-pill d-flex align-items-center">
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={() => changeQty(-1)}
                      >
                        −
                      </button>
                      <span className="qty-value">{qty}</span>
                      <button
                        type="button"
                        className="qty-btn"
                        onClick={() => changeQty(+1)}
                      >
                        +
                      </button>
                    </div>
                  )}

                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-success"
                  >
                    Contacter sur WhatsApp
                  </a>
                </div>

                <div className="mt-3 small text-muted">
                  • Livraison possible via GoShop / partenaire transport
                  <br />
                  • Paiement Mobile Money (TMoney, Flooz…) ou à la livraison
                  selon zones
                </div>
              </div>
            </div>

            {(sku || upc || vendorId) && (
              <div className="card border-0 shadow-sm mb-3">
                <div className="card-body small">
                  <h6 className="fw-semibold mb-2">Détails produit</h6>
                  {sku && (
                    <div>
                      <span className="text-muted">Réf (SKU) : </span>
                      <span className="fw-semibold">{sku}</span>
                    </div>
                  )}
                  {upc && (
                    <div>
                      <span className="text-muted">UPC : </span>
                      <span className="fw-semibold">{upc}</span>
                    </div>
                  )}
                  <div>
                    <span className="text-muted">Vendu par : </span>
                    <span className="fw-semibold">#{vendorName}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-auto pt-2">
              <Link
                to="/catalog"
                className="small text-decoration-none text-muted"
              >
                ← Retour au catalogue
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* =========== DESCRIPTION & INFOS VENDEUR =========== */}
      <div className="row g-4 mt-4">
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <h5 className="mb-3">Description</h5>
              <p
                className="text-muted"
                style={{ whiteSpace: 'pre-line', fontSize: '.95rem' }}
              >
                {desc}
              </p>
            </div>
          </div>
        </div>

        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-body small">
              <h6 className="fw-semibold mb-2">Infos vendeur</h6>
              <p className="mb-1">
                Boutique GoShop / vendeur partenaire{' '}
                <span className="fw-semibold">#{vendorName}</span>.
              </p>
              <p className="text-muted mb-0">
                Les stocks, délais et conditions peuvent varier selon le
                vendeur. Contactez-nous sur WhatsApp pour plus de détails.
              </p>
            </div>
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body small">
              <h6 className="fw-semibold mb-2">Retour & assistance</h6>
              <ul className="mb-0 ps-3">
                <li>Assistance client via WhatsApp ou téléphone.</li>
                <li>
                  Retour possible selon l’état du produit et la politique
                  vendeur.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* =========== BLOCS “COMME WALMART” EN BAS =========== */}
      {mainCategoryId && (
        <div className="mt-4">
          <CategoryProductsStrip
            title="Vous aimerez aussi"
            categoryId={mainCategoryId}
            limit={12}
            viewAllHref={`/catalog?cat=${encodeURIComponent(mainCategoryId)}`}
          />

          <CategoryProductsStrip
            title={
              mainCategoryLabel
                ? `Plus de produits ${mainCategoryLabel}`
                : 'Plus de produits similaires'
            }
            categoryId={mainCategoryId}
            limit={12}
            viewAllHref={`/catalog?cat=${encodeURIComponent(mainCategoryId)}`}
          />
        </div>
      )}
    </div>
  )
}
